import { PMSStore } from "./PMSStore";
import { PMSUser } from "./_models/PMSUser";
import "./PMS.css";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import { CapsuleButton, LogoHeader } from "../Common/_components";
import { getActiveBrand } from "../Common/branding/activeBrand";
import { ColorHelper } from "../Everou/_helpers/ColorHelper";
import { LocationCustomization } from "../Everou/_models/LocationCustomization";
import { WiredPopUpRenderer } from "../Common/PopUps/WiredPopUpRenderer";
import { WiredUserDropdown } from "../Everou/App/WiredUserDropdown";
import { WiredUiAlerts } from "../Common/UiAlerts/WiredUiAlerts";
import { WiredContentWithSidebar } from "../Common/WiredContent/WiredContentWithSidebar";
import { AppWrapper } from "../Common/_components/AppWrapper";
import { PMSStoreSubscriptions } from "./PMSStoreSubscriptions";
import { PMSModulesActions } from "./PMSModulesActions";
import { PMSUserActions } from "./_actions/PMSUserActions";
import { PMSCustomizationActions } from "./_actions/PMSCustomizationActions";
import { PMSOrganizationCustomizationActions } from "./_actions/PMSOrganizationCustomizationActions";
import { useActiveOrganization } from "./_helpers/useActiveOrganization";
import { PMSHeaderOrganizationPicker } from "./_components/PMSHeaderOrganizationPicker";
import { PMSModules } from "./PMSModules";
import { AppInitializationWrapper } from "../Common/_components/AppInitializationWrapper";
import { RentalPopUps } from "../Everou/GlobalPopUps/AppGlobalPopUpsConfig";
import { isAccessCloudBannerEnabled } from "./_constants/accessCloudBanner.constants";
import { useIsNestedDetailOpen } from "../Common/_components/ContentSpaceNesting";
import { Selectors } from "../Everou/_reducers/app.reducer";
import { globalActions, locationActions } from "../Everou/_actions";
import { GLOBAL_POPUPS } from "../Everou/_constants";


const pmsUser = PMSUser()

const testIDs = {
    addCodeButton: "pms-header-add-code-button",
};

PMS.testIDs = testIDs;

export function PMS() {
    //LIBRARIES
    const dispatch = useDispatch();
    const { t } = useTranslation();

    //GLOBAL STATE
    let user = pmsUser;
    user = useSelector(PMSStore.selectors.getPMSUser);
    const isSuperuser = useSelector(Selectors.getEverouUser)?.isSuperuser;

    // Header branding follows the same scope as the Customization module
    // (BAC-215): the active organization's template when the user belongs to
    // one, the personal template as the legacy fallback for those who don't.
    // Reading only the personal one left members of an organization with no
    // branding at all, since creating the organization moves their personal
    // template to it (BAC-15).
    const { activeOrganization, isResolving, hasFailed } = useActiveOrganization();
    const activeOrganizationUid = activeOrganization?.uid ?? null;

    // While a location is open, the header takes its branding: the endpoint
    // already returns the effective values (the location's own, falling back
    // to its organization's or the personal template), which is exactly what
    // the guest sees for that location. The uid guard keeps the previous
    // location's colours from lingering during the fetch.
    const selectedLocationUid = useSelector(PMSStore.selectors.getPMSLocationSelectedUid);
    const locationCustomization = useSelector(PMSStore.selectors.getPMSLocationCustomization);
    const locationCustomizationUid = useSelector(PMSStore.selectors.getPMSLocationCustomizationUid);
    const openLocationCustomization =
        selectedLocationUid && locationCustomizationUid === selectedLocationUid
            ? locationCustomization
            : null;

    const personalCustomization = useSelector(PMSStore.selectors.getPMSCustomizationTemplate);
    const storedOrganizationTemplate = useSelector(
        PMSStore.selectors.getOrganizationCustomizationTemplate,
    );

    let customization = LocationCustomization();
    // The organization template is stored tagged with its uid; ignore it while
    // it still belongs to the previously active organization so the header
    // never paints another organization's colors.
    customization = openLocationCustomization
        ? openLocationCustomization
        : activeOrganizationUid
            ? (storedOrganizationTemplate?.uid === activeOrganizationUid
                ? storedOrganizationTemplate.customization
                : null)
            : personalCustomization;

    //LOCAL STATE
    const [isInitialized, setIsInitialized] = useState(false);
    const [isOpeningCodeEntry, setIsOpeningCodeEntry] = useState(false);

    //INITIALIZATION
    useEffect(() => {
        initialization();

        async function initialization() {
            PMSStoreSubscriptions.subscribe();

            dispatch(PMSModulesActions.setUpModules());
            await dispatch(PMSUserActions.getUser());
            // Re-run now that the user (with its highestPlan) is in state, so
            // Reservations/Organizations are gated by the real plan.
            dispatch(PMSModulesActions.setUpModules());
            setIsInitialized(true);
        }
    }, [dispatch]);

    // Membership opens Organizations regardless of plan, so the sidebar is
    // recomputed once the organizations are known (and if the last one goes).
    const organizationsCount = useSelector(PMSStore.selectors.getOrganizations)?.length ?? 0;
    useEffect(() => {
        if (!isInitialized || isResolving) return;
        dispatch(PMSModulesActions.refreshEnabledModules());
    }, [dispatch, isInitialized, isResolving, organizationsCount]);

    // The modules store is shared with Everou, whose invitation acceptance
    // (reachable from the header's code entry) clears the selected module so
    // its own location resolver picks one. Rental has no such resolver, so
    // fall back to the default module instead of leaving the content blank.
    const selectedModuleId = useSelector(Selectors.getSelectedModuleId);
    useEffect(() => {
        if (!isInitialized || selectedModuleId) return;
        dispatch(PMSModulesActions.selectModule(null));
    }, [dispatch, isInitialized, selectedModuleId]);

    // Fetched once the organizations are known (and again when the active one
    // changes from the picker), so the header shows the branding of the scope
    // actually in use. If the list could not be loaded we still fetch the
    // personal template: unlike the modules, which must not risk editing the
    // wrong scope, here the worst case is showing the user's own branding
    // instead of their organization's — better than no branding at all.
    useEffect(() => {
        if (isResolving && !hasFailed) return;

        if (activeOrganizationUid) {
            dispatch(
                PMSOrganizationCustomizationActions.getTemplate(activeOrganizationUid),
            );
        } else {
            dispatch(PMSCustomizationActions.getPMSCustomizationTemplate());
        }
    }, [dispatch, isResolving, hasFailed, activeOrganizationUid]);

    useEffect(() => {
        const accentColor = customization && customization.accentColor;
        if (accentColor)
            ColorHelper.setCustomColorsFromHue(accentColor);
        else
            ColorHelper.applyBrandColors(getActiveBrand());
    }, [customization]);

    // Gone while a detail is open inside another detail (an account in an
    // organization, an invitation in a location): that pane has half the page
    // at best and the banner's width was what made it not fit.
    const isNestedDetailOpen = useIsNestedDetailOpen();
    const showAccessCloudBanner = isAccessCloudBannerEnabled() && !isNestedDetailOpen;

    //ACTION HANDLERS
    // Same code entry as the Everou header. The flow it opens (subscriptions,
    // cards, invitations) checks the user's Everou locations, which Rental
    // never loads, so fetch them first.
    // A second click while the list is in flight would expire the first call.
    async function handleAddCode() {
        if (isOpeningCodeEntry) return;
        setIsOpeningCodeEntry(true);
        try {
            await dispatch(locationActions.list());
        } finally {
            setIsOpeningCodeEntry(false);
        }
        dispatch(globalActions.showPopUp(GLOBAL_POPUPS.INPUT_CARD_CODE));
    }

    //VIEW
    return (
        <AppWrapper>
            {showAccessCloudBanner &&
                <a
                    className="pms-accesscloud-banner"
                    href="https://planespro.ai360access.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    <div className="pms-accesscloud-banner__media">
                        <img
                            className="pms-accesscloud-banner__image pms-accesscloud-banner__image--static"
                            src={`${process.env.PUBLIC_URL}/banner%20AccessCloud%20Funcionalidades%20v1-1.jpg`}
                            alt="AccessCloud funcionalidades"
                        />
                        <img
                            className="pms-accesscloud-banner__image pms-accesscloud-banner__image--animated"
                            src={`${process.env.PUBLIC_URL}/unnamed.gif`}
                            alt=""
                            aria-hidden="true"
                        />
                    </div>
                </a>
            }
            <WiredPopUpRenderer
                getComponentFn={RentalPopUps.getPopUp}
            />
            <LogoHeader
                content={headerContent()}
                color={customization && customization.headerColor}
                logoSrc={(customization && customization.logo) || getActiveBrand().logoHorizontal}
            />
            <WiredUiAlerts />
            <div className={`pms-accesscloud-main-content ${showAccessCloudBanner ? "" : "pms-accesscloud-main-content--no-banner"}`}>
                <AppInitializationWrapper
                    isInitialized={isInitialized}
                    isSuccessful={!!user}
                    failureMessage={t("mod_dev_not_developer_message")}
                    content={
                        <WiredContentWithSidebar
                            getComponentForModuleId={PMSModules.getComponentForModuleId}
                            getModulesForIds={PMSModules.getModulesForIds}
                            selectModuleAction={PMSModulesActions.selectModule}
                            boldIcons
                        />
                    }
                />
            </div>
        </AppWrapper>
    );

    function headerContent() {
        return (
            <>
            <div className="d-flex align-items-center">
                <PMSHeaderOrganizationPicker activeOrganization={activeOrganization} />
                {isSuperuser &&
                    <CapsuleButton
                        testId={testIDs.addCodeButton}
                        text={t("temp_webapp_add_pack")}
                        onClick={handleAddCode}
                    />
                }
            </div>
            {user && <WiredUserDropdown user={user} />}
            </>
        )
    }

}